import {
	AvailabilityByTime,
	AvailabilityWithReason,
	isTimeSlotWithAvailability,
	ValidityWithReason,
	ValidSlotsByTime,
} from 'common/modules/availabilities/timeSlots/types';
import { Duration, OpeningHoursWithExceptions, OrderProduct, StartTimeSlots } from 'common/types';
import {
	dateIsOutsideOpeningHours,
	getNewEndTime,
	getTimeNearestToNextQuarterHour,
} from 'common/utils/dateCalculations';
import { getHoursAndMinutesFromHourMinuteString } from 'common/utils/dateUtils';
import {
	getExceptionPeriodsBasedOnLocation,
	getOpeningHoursBasedOnLocation,
	getOpeningTimesForDateWithMoments,
} from 'common/utils/shopUtils';
import { groupBy, minBy, uniq } from 'lodash';
import moment from 'moment-timezone';

export const DEFAULT_START_TIME_SLOTS: StartTimeSlots = {
	type: 'interval',
	intervalMinutes: 15,
	orderLimit: null,
};

export const getSelectedProductsWithVariantCounts = (products: OrderProduct[]) => {
	const allVariantIds = products
		.flatMap((p) => p.summary.variantIds)
		.reduce(
			(tot, currId) => ({
				...tot,
				[currId]: (tot[currId] ?? 0) + 1,
			}),
			{} as { [variantId: string]: number },
		);
	const productsByApiId = groupBy(products, 'productApiId');
	const variantsByProductId = Object.entries(productsByApiId).reduce(
		(tot, [productId, orderProducts]) => {
			const productVariantIds = uniq(orderProducts.flatMap((p) => p.summary.variantIds));
			const variantCounts = productVariantIds.reduce((tot, currId) => {
				return {
					...tot,
					[currId]: allVariantIds[currId] ?? 0,
				};
			}, {} as { [variantId: string]: number });
			return {
				...tot,
				[productId]: variantCounts,
			};
		},
		{} as { [productId: string]: { [variantId: string]: number } },
	);
	return variantsByProductId;
};

export const getLocationOpeningHours = ({
	shopOpeningHours,
	locationId,
	mainLocationId,
}: {
	shopOpeningHours: OpeningHoursWithExceptions;
	locationId: string;
	mainLocationId: string;
}) => {
	const locationOpeningHours: OpeningHoursWithExceptions = {
		openingHours: getOpeningHoursBasedOnLocation(shopOpeningHours.openingHours, locationId),
		exceptionPeriods: getExceptionPeriodsBasedOnLocation(
			shopOpeningHours.exceptionPeriods,
			locationId,
			mainLocationId,
			shopOpeningHours.openingHours,
		),
	};
	return locationOpeningHours;
};

/**
 *
 * @param allHours All the TimeSlots that the date is compared to
 * @param date The date that is selected
 * @returns The closest time from the list of timeslots in relation to the date
 * Axel Cedercreutz, 14.9.2021
 */

export const getClosestTimeToDate = (
	allHours: [string, ValidityWithReason][],
	date: moment.Moment,
): moment.Moment | undefined => {
	const timeslot = minBy(allHours, ([hourTime]) => {
		const [hour, minute] = hourTime.split(':');
		return Math.abs(
			moment(date)
				.set({ hour: Number(hour), minute: Number(minute) })
				.diff(date),
		);
	});
	if (!timeslot) return undefined;
	const [time] = timeslot;
	const [hour, minute] = getHoursAndMinutesFromHourMinuteString(time);
	return moment(date).set({ hour: hour, minute: minute });
};

/**
 *  Gets a date with the original timeslot. We want to try to keep the selection the same.
 *  However, if it is outside opening hours, we want to use the opening/closing time instead.
 *  Axel Cedercreutz, 14.9.2021
 */

export const getClosestPossibleTimeSlot = (
	existingSelection: moment.Moment,
	openingHours: OpeningHoursWithExceptions,
): moment.Moment => {
	const openingTimes = getOpeningTimesForDateWithMoments(existingSelection, openingHours);
	if (!openingTimes || openingTimes.isClosed) return existingSelection;
	const existingSelectionOutsideOpeningHours = dateIsOutsideOpeningHours(
		existingSelection,
		openingHours,
	);
	if (existingSelectionOutsideOpeningHours) {
		const dateBeforeOpeningHours = existingSelection.isBefore(openingTimes.openMoment);
		return dateBeforeOpeningHours ? openingTimes.openMoment : openingTimes.closeMoment;
	}
	return existingSelection;
};
const isAvailable = (validity: AvailabilityWithReason | ValidityWithReason) =>
	isTimeSlotWithAvailability(validity) ? validity.units >= 0 : true;

const isOutsideOpeningHours = (validity: AvailabilityWithReason | ValidityWithReason) =>
	validity.valid === false && validity.reason === 'opening_hours';

/*
 * We want to check if a timeslot is available and within opening hours.
 * If we are checking a timeslot that is a start time,
 * we also need to check that the end time is within opening hours.
 * Axel Cedercreutz, 14.9.2021
 */

const isTimeSlotAvailableWithinHours = (
	closestPossibleTimeSlot: moment.Moment,
	validity: AvailabilityWithReason | ValidityWithReason,
	isStartTime?: {
		duration: Duration;
		openingHours: OpeningHoursWithExceptions;
		shopTimeZone: string | undefined;
	},
): boolean => {
	if (!isStartTime) {
		return isAvailable(validity) && !isOutsideOpeningHours(validity);
	}
	const { duration, openingHours, shopTimeZone } = isStartTime;
	const newEndDate = moment(
		getNewEndTime(closestPossibleTimeSlot.toISOString(), duration, openingHours, shopTimeZone),
	);
	return (
		isAvailable(validity) &&
		!isOutsideOpeningHours(validity) &&
		!dateIsOutsideOpeningHours(newEndDate, openingHours)
	);
};

/*
 * We want to check if a timeslot is available and outside opening hours.
 * If we are checking a timeslot that is a start time,
 * we also need to check that either the start or end time is outside opening hours.
 * Axel Cedercreutz, 14.9.2021
 */

const isTimeSlotAvailableOutsideHours = (
	closestPossibleTimeSlot: moment.Moment,
	validity: AvailabilityWithReason | ValidityWithReason,
	isStartTime?: {
		duration: Duration;
		openingHours: OpeningHoursWithExceptions;
		shopTimeZone: string | undefined;
	},
): boolean => {
	if (!isStartTime) {
		return isAvailable(validity) && isOutsideOpeningHours(validity);
	}
	const { duration, openingHours, shopTimeZone } = isStartTime;
	const newEndDate = moment(
		getNewEndTime(closestPossibleTimeSlot.toISOString(), duration, openingHours, shopTimeZone),
	);
	return (
		isAvailable(validity) &&
		(isOutsideOpeningHours(validity) || dateIsOutsideOpeningHours(newEndDate, openingHours))
	);
};

/*
 * We want to check if a specific timeslot is in a list of timeslots and that it is available and within opening hours.
 * Axel Cedercreutz, 14.9.2021
 */

const isClosestTimeSlotAvailableWithinHours = (
	closestPossibleTimeSlot: moment.Moment,
	timeSlotsForDate:
		| {
				[time: string]: AvailabilityWithReason;
		  }
		| {
				[time: string]: ValidityWithReason;
		  },
	isStartTime?: {
		duration: Duration;
		openingHours: OpeningHoursWithExceptions;
		shopTimeZone: string | undefined;
	},
): boolean => {
	const closestPossibleTime = closestPossibleTimeSlot.format('hh:mm');
	return Object.entries(timeSlotsForDate).some(
		([hour, validity]) =>
			hour === closestPossibleTime &&
			isTimeSlotAvailableWithinHours(closestPossibleTimeSlot, validity, isStartTime),
	);
};

/*
 * We want to check if a specific timeslot is in a list of timeslots and that it is available and outside opening hours.
 * Axel Cedercreutz, 14.9.2021
 */

const isClosestTimeSlotAvailableOutsideHours = (
	closestPossibleTimeSlot: moment.Moment,
	timeSlotsForDate:
		| {
				[time: string]: AvailabilityWithReason;
		  }
		| {
				[time: string]: ValidityWithReason;
		  },
	isStartTime?: {
		duration: Duration;
		openingHours: OpeningHoursWithExceptions;
		shopTimeZone: string | undefined;
	},
): boolean => {
	const closestPossibleTime = closestPossibleTimeSlot.format('hh:mm');
	return Object.entries(timeSlotsForDate).some(
		([hour, validity]) =>
			hour === closestPossibleTime &&
			isTimeSlotAvailableOutsideHours(closestPossibleTimeSlot, validity, isStartTime),
	);
};

/*
 * We want to get all the timeslots that are available and within opening hours.
 * Axel Cedercreutz, 14.9.2021
 */

const getTimeSlotsAvailableWithinHours = (
	closestPossibleTimeSlot: moment.Moment,
	timeSlotsForDate:
		| {
				[time: string]: AvailabilityWithReason;
		  }
		| {
				[time: string]: ValidityWithReason;
		  },
	isStartTime?: {
		duration: Duration;
		openingHours: OpeningHoursWithExceptions;
		shopTimeZone: string | undefined;
	},
): [string, ValidityWithReason][] => {
	return Object.entries(timeSlotsForDate).filter(([hourTime, validity]) => {
		return isTimeSlotAvailableWithinHours(closestPossibleTimeSlot, validity, isStartTime);
	});
};

/*
 * We want to get all the timeslots that are available and outside opening hours.
 * Axel Cedercreutz, 14.9.2021
 */

const getTimeSlotsAvailableOutsideHours = (
	closestPossibleTimeSlot: moment.Moment,
	timeSlotsForDate:
		| {
				[time: string]: AvailabilityWithReason;
		  }
		| {
				[time: string]: ValidityWithReason;
		  },
	isStartTime?: {
		duration: Duration;
		openingHours: OpeningHoursWithExceptions;
		shopTimeZone: string | undefined;
	},
): [string, ValidityWithReason][] => {
	return Object.entries(timeSlotsForDate).filter(([hourTime, validity]) =>
		isTimeSlotAvailableOutsideHours(closestPossibleTimeSlot, validity, isStartTime),
	);
};

/**
 *
 * @param closestPossibleTimeSlot The old timeslot, opening or closing time
 * @param timeSlots All timeslots for the date
 * @param isStartTime If the selected time is the start date
 * The return value is defined based on the following logic:
 * 1) closestPossibleTimeSlot if it is Available within opening hours
 * 2) The closest time to closestPossibleTimeSlot that is Available within opening hours
 * 3) closestPossibleTimeSlot if it is Available outside opening hours
 * 4) The closest time to closestPossibleTimeSlot that is Available within opening hours
 * 5) The closest time to closestPossibleTimeSlot that is Available outside opening hours
 * 6) closestPossibleTimeSlot if no time is available during the date
 */

const getPreferredTimeSlot = (
	closestPossibleTimeSlot: moment.Moment,
	timeSlots: AvailabilityByTime | ValidSlotsByTime,
	isStartTime?: {
		duration: Duration;
		openingHours: OpeningHoursWithExceptions;
		shopTimeZone: string | undefined;
	},
): moment.Moment => {
	const dateString = closestPossibleTimeSlot.format('YYYY-MM-DD');
	const timeSlotsForDate = timeSlots[dateString];
	const closestTimeslotIsAvailableWithinHours = isClosestTimeSlotAvailableWithinHours(
		closestPossibleTimeSlot,
		timeSlotsForDate,
		isStartTime,
	);
	if (closestTimeslotIsAvailableWithinHours) return closestPossibleTimeSlot;
	const availableTimesWithinHours = getTimeSlotsAvailableWithinHours(
		closestPossibleTimeSlot,
		timeSlotsForDate,
		isStartTime,
	);
	const closestAvailableTimeSlotWithinHours = getClosestTimeToDate(
		availableTimesWithinHours,
		closestPossibleTimeSlot,
	);
	const closestPossibleTimeslotIsAvailableOutsideHours = isClosestTimeSlotAvailableOutsideHours(
		closestPossibleTimeSlot,
		timeSlotsForDate,
		isStartTime,
	);
	if (closestPossibleTimeslotIsAvailableOutsideHours) {
		return closestAvailableTimeSlotWithinHours ?? closestPossibleTimeSlot;
	}
	const availableTimesOutsideHours = getTimeSlotsAvailableOutsideHours(
		closestPossibleTimeSlot,
		timeSlotsForDate,
		isStartTime,
	);
	const closestTimeSlotAvailableOutsideHours = getClosestTimeToDate(
		availableTimesOutsideHours,
		closestPossibleTimeSlot,
	);
	return (
		closestAvailableTimeSlotWithinHours ??
		closestTimeSlotAvailableOutsideHours ??
		closestPossibleTimeSlot
	);
};

/**
 *
 * @param date selected date
 * @param timeSlots
 * @param duration
 * @param openingHours
 * @param shopTimeZone
 * @param oldTimeSlot
 * @param isEndTime If the selected date is the end time
 * @returns The new selected date with the best possible timeslot.
 * Checks if the possible timeslot is before to the minDate (i.e. in NewRental moment() rounded to nearest quarter).
 * if it is returns the minDate.
 */

export const getPreferredTimeSlotForDate = (
	date: moment.Moment,
	timeSlots: AvailabilityByTime | ValidSlotsByTime,
	duration: Duration,
	openingHours: OpeningHoursWithExceptions,
	shopTimeZone: string | undefined,
	oldTimeSlot: {
		hours: number;
		minutes: number;
	},
	isEndTime: boolean,
	minDate: moment.Moment,
): moment.Moment => {
	const dateWithOldTimeSlot = moment(date).set({
		hour: oldTimeSlot.hours,
		minute: oldTimeSlot.minutes,
	});
	const closestPossibleTimeSlot = getClosestPossibleTimeSlot(dateWithOldTimeSlot, openingHours);
	const preferredTimeSlot = getPreferredTimeSlot(
		closestPossibleTimeSlot,
		timeSlots,
		!isEndTime ? { duration, openingHours, shopTimeZone } : undefined,
	);
	const nearestQuarterHourForMinDate = isEndTime
		? getTimeNearestToNextQuarterHour(moment(minDate).add(15, 'minutes')) // minDate is the starttime => need to add 15 min for the rental to have a duration
		: getTimeNearestToNextQuarterHour(minDate);
	return nearestQuarterHourForMinDate.isAfter(preferredTimeSlot)
		? nearestQuarterHourForMinDate
		: preferredTimeSlot;
};
